<template>
  <Dialog
    v-model:visible="dialogVisible"
    modal
    :draggable="false"
    class="custom-dialog fixNotchPaddingTop procedure-request-dialog"
    :style="{ width: 'min(760px, calc(100vw - 24px))' }"
    :breakpoints="{ '768px': '100vw' }"
    :contentStyle="{ paddingTop: '0', overflow: 'hidden' }"
    :closable="!attachmentLoading && !actionLoading"
  >
    <input
      ref="attachmentInput"
      type="file"
      class="procedure-request-dialog__hidden-input"
      @change="handleAttachmentSelected"
    />

    <template #header>
      <div class="procedure-request-dialog__header">
        <div class="procedure-request-dialog__header-copy">
          <div class="procedure-request-dialog__icon">
            <i :class="iconClass" aria-hidden="true"></i>
          </div>
          <div class="procedure-request-dialog__title-block">
            <span class="procedure-request-dialog__eyebrow">
              {{ definition?.name || t("wkt.client.procedures.title") }}
            </span>
            <h2 class="procedure-request-dialog__title">
              {{ request?.requestTitle || definition?.name || t("wkt.client.procedures.title") }}
            </h2>
          </div>
        </div>

        <Tag
          v-if="request?.requestStatus != null"
          :severity="statusSeverity"
          :value="statusLabel"
        />
      </div>
    </template>

    <div class="procedure-request-dialog__body" :data-mode="mode">
      <div v-if="loading" class="procedure-request-dialog__state">
        <div class="procedure-request-dialog__skeleton">
          <div class="procedure-request-dialog__skeleton-card">
            <Skeleton width="10rem" height="0.95rem" borderRadius="999px" />
            <Skeleton width="100%" height="1.2rem" borderRadius="999px" />
            <Skeleton width="7rem" height="1.4rem" borderRadius="999px" />
          </div>
          <div class="procedure-request-dialog__skeleton-grid">
            <Skeleton
              v-for="index in 6"
              :key="index"
              width="100%"
              height="5rem"
              borderRadius="18px"
            />
          </div>
        </div>
      </div>

      <div v-else-if="error || notFound" class="procedure-request-dialog__state">
        <EmptyState
          :title="notFound ? t('wkt.client.procedures.item.not.available') : t('wkt.client.procedures.error-state.title')"
          :description="notFound ? t('wkt.client.procedures.detail.not_available_description') : t('wkt.client.procedures.error-state.description')"
        />

        <Button
          v-if="!notFound"
          class="footer-primary-button"
          text
          :label="t('wkt.client.procedures.actions.retry')"
          @click="$emit('retry')"
        />
      </div>

      <template v-else-if="request || showMutableBase">
        <div class="procedure-request-dialog__summary">
          <div v-if="request?.adminId" class="procedure-request-dialog__meta-note">
            {{ t("wkt.client.procedures.created_by_admin") }}
          </div>

          <div
            v-if="showLegacyVacationReadOnlyNotice"
            class="procedure-request-dialog__warning"
          >
            {{ t("wkt.client.procedures.detail.legacy_vacation_read_only") }}
          </div>

          <div v-if="request" class="procedure-request-dialog__grid">
            <div v-if="request.requestDate" class="procedure-request-dialog__cell">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.procedure-card.requested_date") }}
              </span>
              <span class="procedure-request-dialog__value">
                {{ formatLongDateTime(request.requestDate, true) }}
              </span>
            </div>

            <div v-if="request.updateDate" class="procedure-request-dialog__cell">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.detail.updated_date") }}
              </span>
              <span class="procedure-request-dialog__value">
                {{ formatLongDateTime(request.updateDate, true) }}
              </span>
            </div>

            <div v-if="employeeName" class="procedure-request-dialog__cell">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.table.headers.name") }}
              </span>
              <span class="procedure-request-dialog__value">{{ employeeName }}</span>
            </div>

            <div v-if="request.requestReference" class="procedure-request-dialog__cell">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.detail.request_reference") }}
              </span>
              <span class="procedure-request-dialog__value">{{ request.requestReference }}</span>
            </div>

            <div
              v-if="request.fromDate && formState.visibility.showFromDate && !formState.editability.fromDate"
              class="procedure-request-dialog__cell"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.fromDateLabel || t("wkt.client.procedures.procedure-card.start_date") }}
              </span>
              <span class="procedure-request-dialog__value">{{ formatSimpleDate(request.fromDate) }}</span>
            </div>

            <div
              v-if="request.toDate && formState.visibility.showToDate && !formState.editability.toDate"
              class="procedure-request-dialog__cell"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.toDateLabel || t("wkt.client.procedures.procedure-card.end_date") }}
              </span>
              <span class="procedure-request-dialog__value">{{ formatSimpleDate(request.toDate) }}</span>
            </div>

            <div
              v-if="request.fromHour && formState.visibility.showFromTime && !formState.editability.fromTime"
              class="procedure-request-dialog__cell"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.fromTimeLabel || t("wkt.client.procedures.edit-window.start_time") }}
              </span>
              <span class="procedure-request-dialog__value">{{ formatProcedureTime(request.fromHour) }}</span>
            </div>

            <div
              v-if="request.toHour && formState.visibility.showToTime && !formState.editability.toTime"
              class="procedure-request-dialog__cell"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.toTimeLabel || t("wkt.client.procedures.edit-window.end_time") }}
              </span>
              <span class="procedure-request-dialog__value">{{ formatProcedureTime(request.toHour) }}</span>
            </div>

            <div
              v-if="projectLabel && formState.visibility.showProject && !formState.editability.project"
              class="procedure-request-dialog__cell"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.projectLabel || t("wkt.client.procedures.cbo_projects") }}
              </span>
              <span class="procedure-request-dialog__value">{{ projectLabel }}</span>
            </div>

            <div
              v-if="
                request.activityTypeDescription &&
                formState.visibility.showActivityType &&
                !formState.editability.activityType
              "
              class="procedure-request-dialog__cell"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.activityTypeLabel || t("wkt.client.procedures.detail.activity_type") }}
              </span>
              <span class="procedure-request-dialog__value">{{ request.activityTypeDescription }}</span>
            </div>

            <div
              v-if="request.quantity != null && formState.visibility.showQuantity && !formState.editability.quantity"
              class="procedure-request-dialog__cell"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.quantityLabel || t("wkt.client.procedures.edit-window.quantity_placeholder") }}
              </span>
              <span class="procedure-request-dialog__value">{{ request.quantity }}</span>
            </div>

            <div v-if="hoursBagLabel && !showHoursBagSelect" class="procedure-request-dialog__cell">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.bag_hours") }}
              </span>
              <span class="procedure-request-dialog__value">{{ hoursBagLabel }}</span>
            </div>

            <div v-if="absenceCategoryLabel && !showAbsenceCategorySelect" class="procedure-request-dialog__cell">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.edit-window.absence_category_label") }}
              </span>
              <span class="procedure-request-dialog__value">{{ absenceCategoryLabel }}</span>
            </div>

            <div
              v-for="detail in balanceDetails"
              :key="detail.label"
              class="procedure-request-dialog__cell"
            >
              <span class="procedure-request-dialog__label">{{ detail.label }}</span>
              <span
                :class="[
                  'procedure-request-dialog__value',
                  detail.tone ? `procedure-request-dialog__value--${detail.tone}` : '',
                ]"
              >
                {{ detail.value }}
              </span>
            </div>
          </div>
        </div>

        <section v-if="showMutableBase" class="procedure-request-dialog__section">
          <h3 class="procedure-request-dialog__section-title">
            {{ t("wkt.client.procedures.title") }}
          </h3>

          <div class="procedure-request-dialog__form-grid">
            <label
              v-if="showFreeTextFieldInForm"
              class="procedure-request-dialog__field procedure-request-dialog__field--full"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.freeTextLabel || t("wkt.client.procedures.table.headers.request") }}
              </span>
              <Textarea
                v-model="draft.requestInfo"
                rows="4"
                autoResize
                :disabled="!formState.editability.freeText"
              />
            </label>

            <label v-if="showTitleField" class="procedure-request-dialog__field">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.edit-window.title_placeholder") }}
              </span>
              <InputText
                v-model.trim="draft.requestTitle"
                :disabled="!formState.editability.requestTitle"
                :placeholder="t('wkt.client.procedures.edit-window.title_placeholder')"
              />
            </label>

            <label
              v-if="formState.visibility.showFromDate"
              class="procedure-request-dialog__field"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.fromDateLabel || t("wkt.client.procedures.edit-window.start_date_placeholder") }}
              </span>
              <DatePicker
                :modelValue="draft.fromDate"
                :disabled="!formState.editability.fromDate"
                showIcon
                iconDisplay="input"
                fluid
                dateFormat="dd/mm/yy"
                @update:modelValue="draft.fromDate = asDateValue($event)"
              />
            </label>

            <label
              v-if="formState.visibility.showToDate"
              class="procedure-request-dialog__field"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.toDateLabel || t("wkt.client.procedures.edit-window.end_date_placeholder") }}
              </span>
              <DatePicker
                :modelValue="draft.toDate"
                :disabled="!formState.editability.toDate"
                showIcon
                iconDisplay="input"
                fluid
                dateFormat="dd/mm/yy"
                @update:modelValue="draft.toDate = asDateValue($event)"
              />
            </label>

            <label
              v-if="formState.visibility.showFromTime"
              class="procedure-request-dialog__field"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.fromTimeLabel || t("wkt.client.procedures.edit-window.start_time") }}
              </span>
              <InputText
                v-model="draft.fromTimeText"
                type="time"
                :disabled="!formState.editability.fromTime"
              />
            </label>

            <label
              v-if="formState.visibility.showToTime"
              class="procedure-request-dialog__field"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.toTimeLabel || t("wkt.client.procedures.edit-window.end_time") }}
              </span>
              <InputText
                v-model="draft.toTimeText"
                type="time"
                :disabled="!formState.editability.toTime"
              />
            </label>

            <label
              v-if="showManualVacationDaysField"
              class="procedure-request-dialog__field"
            >
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.edit-window.vacations") }}
              </span>
              <InputText
                v-model.trim="draft.vacationDaysText"
                inputmode="decimal"
                :disabled="!manualVacationDaysEditable"
                :placeholder="t('wkt.client.procedures.edit-window.vacations')"
              />
            </label>

            <label
              v-if="formState.visibility.showQuantity"
              class="procedure-request-dialog__field"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.quantityLabel || t("wkt.client.procedures.edit-window.quantity_placeholder") }}
              </span>
              <InputText
                v-model.trim="draft.quantity"
                inputmode="decimal"
                :disabled="!formState.editability.quantity"
                :placeholder="definition?.quantityLabel || t('wkt.client.procedures.edit-window.quantity_placeholder')"
              />
            </label>

            <label
              v-if="formState.visibility.showProject"
              class="procedure-request-dialog__field"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.projectLabel || t("wkt.client.procedures.cbo_projects") }}
              </span>
              <Select
                v-model="draft.projectId"
                :options="resolvedProjectOptions"
                optionLabel="label"
                optionValue="value"
                :loading="projectOptionsLoading"
                :disabled="!formState.editability.project"
                :placeholder="t('wkt.client.procedures.edit-window.select_project')"
              />
            </label>

            <label
              v-if="formState.visibility.showActivityType"
              class="procedure-request-dialog__field"
            >
              <span class="procedure-request-dialog__label">
                {{ definition?.activityTypeLabel || t("wkt.client.procedures.detail.activity_type") }}
              </span>
              <Select
                v-model="draft.activityTypeId"
                :options="activityTypeOptions"
                optionLabel="label"
                optionValue="value"
                :disabled="!formState.editability.activityType"
              />
            </label>

            <label
              v-if="showHoursBagSelect"
              class="procedure-request-dialog__field"
            >
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.bag_hours") }}
              </span>
              <Select
                v-model="draft.hoursBagType"
                :options="hoursBagOptions"
                optionLabel="label"
                optionValue="value"
                :disabled="!formState.editability.hoursBag"
              />
            </label>

            <label
              v-if="showAbsenceCategorySelect"
              class="procedure-request-dialog__field"
            >
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.edit-window.absence_category_label") }}
              </span>
              <Select
                v-model="draft.paidAbsenceCategoryId"
                :options="absenceCategoryOptions"
                optionLabel="label"
                optionValue="value"
                :loading="absenceCategoryOptionsLoading"
                :disabled="!formState.editability.absenceCategory"
              />
            </label>
          </div>
        </section>

        <section
          v-if="showVacationSupportSection"
          class="procedure-request-dialog__section"
        >
          <div class="procedure-request-dialog__section-header">
            <h3 class="procedure-request-dialog__section-title">
              {{ t("wkt.client.procedures.edit-window.vacations") }}
            </h3>
            <Button
              v-if="showVacationCalendarAction"
              class="footer-primary-button"
              text
              :label="t('wkt.client.procedures.procedure-card.show_my_calendar')"
              @click="$emit('calendar')"
            />
          </div>

          <p
            v-if="vacationPeriodLabel"
            class="procedure-request-dialog__vacation-summary"
          >
            {{ vacationPeriodLabel }}
          </p>

          <p
            v-if="vacationHoursSummaryLabel"
            class="procedure-request-dialog__vacation-summary"
          >
            {{ vacationHoursSummaryLabel }}
          </p>

          <div
            v-if="showVacationBalanceSection"
            class="procedure-request-dialog__vacation-balance"
          >
            <div class="procedure-request-dialog__vacation-balance-cell">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.edit-window.current_balance") }}
              </span>
              <strong class="procedure-request-dialog__vacation-balance-value">
                {{ formatDaysAmount(vacationSupport.currentBalance.value) }}
              </strong>
            </div>

            <div class="procedure-request-dialog__vacation-balance-cell">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.edit-window.requests_pending") }}
              </span>
              <strong class="procedure-request-dialog__vacation-balance-value">
                {{ formatDaysAmount(vacationSupport.requestsPending.value) }}
              </strong>
            </div>

            <div class="procedure-request-dialog__vacation-balance-cell">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.edit-window.request_current") }}
              </span>
              <strong
                class="procedure-request-dialog__vacation-balance-value procedure-request-dialog__vacation-balance-value--negative"
              >
                {{ formatDaysAmount(vacationSupport.requestCurrent.value) }}
              </strong>
            </div>

            <div class="procedure-request-dialog__vacation-balance-cell">
              <span class="procedure-request-dialog__label">
                {{ t("wkt.client.procedures.edit-window.final_balance") }}
              </span>
              <strong
                :class="[
                  'procedure-request-dialog__vacation-balance-value',
                  vacationSupport.finalBalance.value != null && vacationSupport.finalBalance.value < 0
                    ? 'procedure-request-dialog__vacation-balance-value--negative'
                    : 'procedure-request-dialog__vacation-balance-value--positive',
                ]"
              >
                {{ formatDaysAmount(vacationSupport.finalBalance.value) }}
              </strong>
            </div>
          </div>

          <div
            v-if="vacationSupport.negativeBalanceWarning.value"
            class="procedure-request-dialog__warning"
          >
            {{ t("wkt.client.procedures.edit-window.balance_insufficient") }}
          </div>

          <div
            v-if="vacationSupport.missingCalendarConfiguration.value"
            class="procedure-request-dialog__warning"
          >
            {{ t("wkt.client.procedures.edit-window.error_not_calendar") }}
          </div>
        </section>

        <section
          v-if="showFreeTextSection"
          class="procedure-request-dialog__section"
        >
          <h3 class="procedure-request-dialog__section-title">
            {{ definition?.freeTextLabel || t("wkt.client.procedures.table.headers.request") }}
          </h3>
          <Textarea
            v-if="formState.editability.freeText"
            v-model="draft.requestInfo"
            rows="4"
            autoResize
            :disabled="!formState.editability.freeText"
          />
          <p v-else class="procedure-request-dialog__section-copy">{{ request?.requestInfo }}</p>
        </section>

        <section
          v-if="showApproverCommentSection"
          class="procedure-request-dialog__section"
        >
          <h3 class="procedure-request-dialog__section-title">
            {{ t("wkt.client.procedures.procedure-card.comments") }}
          </h3>
          <Textarea
            v-if="formState.editability.approverComment"
            v-model="draft.approverComment"
            rows="4"
            autoResize
            :disabled="!formState.editability.approverComment"
          />
          <p v-else class="procedure-request-dialog__section-copy">{{ request?.approverComment }}</p>
        </section>

        <section v-if="showDocumentsSection" class="procedure-request-dialog__section">
          <h3 class="procedure-request-dialog__section-title">
            {{ t("wkt.client.procedures.table.headers.attached") }}
          </h3>

          <div
            v-if="showUploadAttachmentAction"
            class="procedure-request-dialog__documents-actions"
          >
            <Button
              class="footer-primary-button procedure-request-dialog__action-button"
              text
              severity="info"
              icon="pi pi-upload"
              :label="t('wkt.client.procedures.edit-window.upload_document')"
              :loading="attachmentLoading"
              :disabled="attachmentLoading || actionLoading"
              @click="openAttachmentPicker"
            />
          </div>

          <div class="procedure-request-dialog__documents">
            <button
              v-for="document in resolvedDocuments"
              :key="document.id ?? document.documentName ?? 'document'"
              type="button"
              class="procedure-request-dialog__document"
              @click="downloadDocument(document.id, document.documentName)"
            >
              <i class="pi pi-paperclip" aria-hidden="true"></i>
              <span>{{ document.documentName || "---" }}</span>
            </button>

            <Button
              v-for="document in removableDocuments"
              :key="`remove-${document.id ?? document.documentName ?? 'document'}`"
              class="procedure-request-dialog__action-button procedure-request-dialog__document-remove"
              text
              severity="danger"
              icon="pi pi-trash"
              :label="t('wkt.client.procedures.edit-window.remove_document')"
              :disabled="attachmentLoading || actionLoading"
              @click="emitRemoveDocument(document.id)"
            />
          </div>
        </section>

        <div
          v-if="request?.expenseSheetId"
          class="procedure-request-dialog__footer-actions"
        >
          <Button
            class="footer-primary-button"
            text
            :label="t('wkt.client.procedures.procedure-card.show_expense_sheet')"
            @click="openExpenseSheetDetail"
          />
        </div>
      </template>
    </div>

    <template #footer>
      <div class="procedure-request-dialog__footer-content">
        <Button
          v-if="showDeclineAction"
          class="procedure-request-dialog__action-button"
          text
          severity="danger"
          :label="t('wkt.client.common.dialog-buttons.decline')"
          :disabled="actionLoading || attachmentLoading"
          @click="emitDecline"
        />
        <Button
          v-if="showDeleteAction"
          class="procedure-request-dialog__action-button"
          text
          severity="danger"
          :label="t('wkt.client.common.dialog-buttons.delete')"
          :disabled="actionLoading || attachmentLoading"
          @click="emitDelete"
        />
        <div class="procedure-request-dialog__footer-spacer"></div>
        <Button
          v-if="showMutableBase"
          class="footer-primary-button"
          text
          :label="footerConfirmLabel"
          :loading="actionLoading"
          :disabled="attachmentLoading || submitBlockedByBalance"
          @click="emitSubmit"
        />
        <Button
          class="footer-button-cancel"
          text
          :label="footerCloseLabel"
          :disabled="actionLoading || attachmentLoading"
          @click="dialogVisible = false"
        />
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, reactive, ref, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import { gwtApi } from "@wkt/api/gwt/gwtAPI";
import type { Project, SessionInformation } from "@wkt/api/gwt/gwtDTO";
import {
  type FullProcedureDefinitionDTO,
  type ProcedureDocumentDTO,
  type FullEmployeeDTO,
  type FullProcedureRequestDTO,
  type ProcedureTimeDTO,
} from "@wkt/api/procedures";
import EmptyState from "@wkt/components/common/EmptyState.vue";
import { useDateConverter } from "@wkt/composables/useDateConverter";
import { useDateFormatter } from "@wkt/composables/useDateFormatter";
import {
  type ProcedureRequestDialogDraftState,
  type ProcedureRequestDialogMode,
  type ProcedureRequestDialogSubmitPayload,
} from "./procedureRequestDialogContract";
import { useProcedureRequestForm } from "./useProcedureRequestForm";
import { useProcedureVacationSupport } from "./useProcedureVacationSupport";

const props = defineProps<{
  visible: boolean;
  mode: ProcedureRequestDialogMode;
  request: FullProcedureRequestDTO | null;
  definition: FullProcedureDefinitionDTO | null;
  draftSeed?: ProcedureRequestDialogDraftState | null;
  approverEmployee?: FullEmployeeDTO | null;
  projectOptions?: Array<{ label: string; value: number }>;
  projectOptionsLoading?: boolean;
  absenceCategoryOptions?: Array<{ label: string; value: number | null }>;
  absenceCategoryOptionsLoading?: boolean;
  attachmentLoading?: boolean;
  actionLoading?: boolean;
  loading: boolean;
  error: boolean;
  notFound: boolean;
  sessionInformation?: SessionInformation | null;
}>();

const emit = defineEmits<{
  "update:visible": [value: boolean];
  retry: [];
  expense: [expenseSheetId: number];
  calendar: [];
  submit: [payload: ProcedureRequestDialogSubmitPayload];
  "draft-change": [payload: ProcedureRequestDialogDraftState];
  "upload-document": [file: File];
  "remove-document": [payload: { documentId: number }];
  delete: [];
  decline: [payload: ProcedureRequestDialogSubmitPayload];
}>();

const { t } = useI18n();
const { convertDateToSimpleDate, convertSimpleDateToDate, convertSimpleTimeToHHMM } = useDateConverter();
const { formatLongDateTime, formatSimpleDate } = useDateFormatter();
const sessionInformation = computed<SessionInformation | null>(() => props.sessionInformation || null);
const attachmentInput = ref<HTMLInputElement | null>(null);
const { state: formState, openContext } = useProcedureRequestForm({
  request: toRef(props, "request"),
  definition: toRef(props, "definition"),
  mode: toRef(props, "mode"),
});

interface SelectOption {
  label: string;
  value: number;
}

interface ActivityTypeOption {
  id: number;
  description?: string | null;
  enabled?: boolean | null;
}

interface ProcedureRequestDialogDraft {
  requestTitle: string;
  requestInfo: string;
  fromDate: Date | null;
  toDate: Date | null;
  fromTimeText: string;
  toTimeText: string;
  vacationDaysText: string;
  quantity: string;
  projectId: number | null;
  activityTypeId: number | null;
  hoursBagType: number | null;
  paidAbsenceCategoryId: number | null;
  approverComment: string;
}

const REQUEST_SUBTYPE_COMPLETE_DAY = 7;
const VACATION_COUNT_NONE = 0;

const draft = reactive<ProcedureRequestDialogDraft>(createDraft());
const vacationSupport = useProcedureVacationSupport({
  visible: toRef(props, "visible"),
  mode: toRef(props, "mode"),
  request: toRef(props, "request"),
  definition: toRef(props, "definition"),
  draft,
  sessionInformation,
  approverEmployee: toRef(props, "approverEmployee"),
});

watch(
  [formState, toRef(props, "draftSeed")],
  ([, draftSeed]) => {
    Object.assign(draft, createDraft(draftSeed || null));
  },
  { immediate: true }
);

watch(
  draft,
  () => {
    emit("draft-change", buildDraftState());
  },
  { deep: true, immediate: true }
);

const dialogVisible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit("update:visible", value),
});

const employeeName = computed(() => {
  const firstname = props.request?.firstname?.trim() || "";
  const lastname = props.request?.lastname?.trim() || "";
  const fullName = `${firstname} ${lastname}`.trim();
  return fullName || "";
});

const showMutableBase = computed(() => openContext.value.mode !== "read-only");

const showTitleField = computed(
  () => showMutableBase.value || Boolean(props.request?.requestTitle)
);

const showFreeTextFieldInForm = computed(
  () =>
    showMutableBase.value &&
    formState.value.visibility.showFreeText &&
    formState.value.editability.freeText
);

const showFreeTextSection = computed(() => {
  if (!formState.value.visibility.showFreeText) {
    return false;
  }

  if (showFreeTextFieldInForm.value) {
    return false;
  }

  return Boolean(props.request?.requestInfo);
});

const showApproverCommentSection = computed(() => {
  if (formState.value.editability.approverComment) {
    return true;
  }

  return Boolean(props.request?.approverComment);
});

const showLegacyVacationReadOnlyNotice = computed(
  () => formState.value.flags.isLegacyVacationRequest
);

const showDeleteAction = computed(
  () => showMutableBase.value && formState.value.editability.deleteAction && openContext.value.mode === "edit"
);

const showDeclineAction = computed(
  () => showMutableBase.value && openContext.value.mode === "approve"
);

const showVacationSupportSection = computed(
  () => showMutableBase.value && vacationSupport.isVacationLeave.value
);

const showManualVacationDaysField = computed(
  () => showVacationSupportSection.value && vacationSupport.showManualDaysInput.value
);

const manualVacationDaysEditable = computed(
  () => formState.value.editability.hoursBag && openContext.value.mode !== "approve"
);

const showUploadAttachmentAction = computed(
  () =>
    showMutableBase.value &&
    formState.value.editability.attachments &&
    formState.value.visibility.allowUploadDocument
);

const showDocumentsSection = computed(
  () => Boolean(props.request?.documents?.length) || showUploadAttachmentAction.value
);

const removableDocuments = computed<ProcedureDocumentDTO[]>(() => {
  if (!showUploadAttachmentAction.value) {
    return [];
  }

  return (props.request?.documents || []).filter((document) => document.id != null);
});

const resolvedDocuments = computed<ProcedureDocumentDTO[]>(
  () => props.request?.documents || []
);

const showVacationCalendarAction = computed(
  () => showVacationSupportSection.value && vacationSupport.showOwnCalendarAction.value
);

const showVacationBalanceSection = computed(
  () => showVacationSupportSection.value && vacationSupport.showVacationBalance.value
);

const vacationPeriodLabel = computed(() => {
  if (vacationSupport.showManualDaysInput.value) {
    return "";
  }

  const requestDays = vacationSupport.requestDayEquivalent.value;
  if (requestDays == null) {
    return "";
  }

  const modeKey =
    vacationSupport.effectiveCountMode.value === 2
      ? requestDays === 1
        ? "wkt.client.procedures.working_1"
        : "wkt.client.procedures.working_2"
      : requestDays === 1
        ? "wkt.client.procedures.natural_1"
        : "wkt.client.procedures.natural_2";

  return `${t("wkt.client.procedures.total")} ${formatDecimal(requestDays)} ${requestDays === 1 ? t("wkt.client.procedures.day") : t("wkt.client.procedures.days")} ${t(modeKey)}`.trim();
});

const vacationHoursSummaryLabel = computed(() => {
  if (!vacationSupport.isVacationByHours.value) {
    return "";
  }

  const resolvedRequestedHours = vacationSupport.requestedHours.value;
  const resolvedWorkdayHours = vacationSupport.workdayHours.value;
  const requestDays = vacationSupport.requestDayEquivalent.value;

  if (
    resolvedRequestedHours == null ||
    resolvedWorkdayHours == null ||
    requestDays == null
  ) {
    return "";
  }

  return formatTemplate(
    t("wkt.client.procedures.edit-window.summary_hours"),
    formatDecimal(resolvedRequestedHours),
    formatDecimal(resolvedWorkdayHours),
    formatDecimal(requestDays)
  );
});

const footerCloseLabel = computed(() =>
  showMutableBase.value
    ? t("wkt.client.common.dialog-buttons.cancel")
    : t("wkt.client.common.dialog-buttons.close")
);

const footerConfirmLabel = computed(() =>
  openContext.value.mode === "approve"
    ? t("wkt.client.common.dialog-buttons.approve")
    : t("wkt.client.common.dialog-buttons.send")
);

const submitBlockedByBalance = computed(
  () =>
    openContext.value.mode !== "approve" &&
    vacationSupport.balanceInsufficient.value
);

const iconClass = computed(() => {
  const iconType = props.definition?.iconType || "pi pi-file";
  return iconType.startsWith("pi ") ? iconType : "pi pi-file";
});

const statusLabel = computed(() => {
  if (openContext.value.mode === "create") {
    return t("wkt.client.procedures.procedure-card.preparing_status");
  }

  switch (props.request?.requestStatus) {
    case 0:
      return t("wkt.client.procedures.procedure-card.pending_status");
    case 1:
      return t("wkt.client.procedures.procedure-card.approved_status");
    case 2:
      return t("wkt.client.procedures.procedure-card.declined_status");
    case 3:
      return t("wkt.client.procedures.procedure-card.canceled_status");
    case 4:
      return t("wkt.client.procedures.procedure-card.completed_status");
    default:
      return "---";
  }
});

const statusSeverity = computed(() => {
  switch (props.request?.requestStatus) {
    case 0:
      return "warn";
    case 1:
      return "success";
    case 2:
      return "danger";
    case 4:
      return "info";
    default:
      return "secondary";
  }
});

const projectLabel = computed(() => {
  if (!props.request?.projectId) {
    return "";
  }

  const shortName = props.request.projectShortName?.trim();
  const description = props.request.projectDescription?.trim();

  if (shortName && description) {
    return `${shortName} - ${description}`;
  }

  return shortName || description || "---";
});

const projectOptions = computed<SelectOption[]>(() => {
  const options = new Map<number, SelectOption>();
  const projects = (sessionInformation.value?.employeeProjects || []) as Project[];

  projects.forEach((project) => {
    if (project?.id == null || project.enabled === false) {
      return;
    }

    options.set(project.id, {
      value: project.id,
      label: formatProjectLabel(project.shortName, project.description, project.name),
    });
  });

  if (props.request?.projectId != null && !options.has(props.request.projectId)) {
    options.set(props.request.projectId, {
      value: props.request.projectId,
      label: formatProjectLabel(
        props.request.projectShortName,
        props.request.projectDescription,
        String(props.request.projectId)
      ),
    });
  }

  return Array.from(options.values()).sort((left, right) => left.label.localeCompare(right.label));
});

const resolvedProjectOptions = computed<SelectOption[]>(() => {
  if (props.projectOptions?.length) {
    return props.projectOptions;
  }

  return projectOptions.value;
});

const activityTypeOptions = computed<SelectOption[]>(() => {
  const options = ((sessionInformation.value?.employeeActivityType || []) as ActivityTypeOption[])
    .filter((activityType) => activityType?.id != null && activityType.enabled !== false)
    .map((activityType) => ({
      value: activityType.id,
      label: activityType.description?.trim() || String(activityType.id),
    }));

  if (
    props.request?.activityTypeId != null &&
    props.request.activityTypeDescription &&
    !options.some((option) => option.value === props.request?.activityTypeId)
  ) {
    options.push({
      value: props.request.activityTypeId,
      label: props.request.activityTypeDescription,
    });
  }

  return options.sort((left, right) => left.label.localeCompare(right.label));
});

const hoursBagOptions = computed<Array<{ label: string; value: number | null }>>(() => {
  const options = formState.value.allowedHoursBagTypeIds.map((hoursBagTypeId) => ({
    value: hoursBagTypeId,
    label: getHoursBagLabel(hoursBagTypeId),
  }));

  if (
    props.request?.hoursBagType != null &&
    !options.some((option) => option.value === props.request?.hoursBagType)
  ) {
    options.push({
      value: props.request.hoursBagType,
      label: getHoursBagLabel(props.request.hoursBagType),
    });
  }

  return options;
});

const showHoursBagSelect = computed(
  () => showMutableBase.value && formState.value.visibility.showVacationDays && hoursBagOptions.value.length > 0
);

const showAbsenceCategorySelect = computed(
  () =>
    showMutableBase.value &&
    formState.value.flags.showAbsenceCategory &&
    Boolean(props.absenceCategoryOptions?.length)
);

watch(
  () => hoursBagOptions.value,
  (options) => {
    if (
      showHoursBagSelect.value &&
      draft.hoursBagType == null &&
      options.length === 1
    ) {
      draft.hoursBagType = options[0].value;
    }
  },
  { immediate: true }
);

watch(
  () => props.absenceCategoryOptions,
  (options) => {
    const validCategoryOptions = (options || []).filter(
      (option) => option.value != null
    );

    if (
      showAbsenceCategorySelect.value &&
      draft.paidAbsenceCategoryId == null &&
      validCategoryOptions.length === 1
    ) {
      draft.paidAbsenceCategoryId = validCategoryOptions[0].value;
    }
  },
  { immediate: true, deep: true }
);

const hoursBagLabel = computed(() => {
  const hoursBagType = props.request?.hoursBagType;
  if (hoursBagType != null) {
    return getHoursBagLabel(hoursBagType);
  }

  return "";
});

const absenceCategoryLabel = computed(() => {
  if (!formState.value.flags.showAbsenceCategory) {
    return "";
  }

  return (
    props.request?.paidAbsenceCategoryName?.trim() ||
    t("wkt.client.procedures.edit-window.no_absence_category")
  );
});

const balanceDetails = computed(() => {
  const request = props.request;
  if (
    !request ||
    (
      request.hoursBagDelta == null ||
      request.hoursBagDelta === 0
    ) &&
    (
      request.vacationDeltaOld == null ||
      request.vacationDeltaOld === 0
    ) &&
    (
      request.extraDeltaOld == null ||
      request.extraDeltaOld === 0
    )
  ) {
    return [];
  }

  const details: Array<{ label: string; value: string; tone?: "positive" | "negative" }> = [];

  if (request.vacationDeltaOld != null && request.vacationDeltaOld !== 0) {
    details.push({
      label: t("wkt.client.procedures.procedure-card.vacations"),
      value: formatSignedDays(request.vacationDeltaOld),
      tone: getTone(request.vacationDeltaOld),
    });
  }

  if (request.extraDeltaOld != null && request.extraDeltaOld !== 0) {
    details.push({
      label: t("wkt.client.vacations.free_days"),
      value: formatSignedDays(request.extraDeltaOld),
      tone: getTone(request.extraDeltaOld),
    });
  }

  if (request.hoursBagDelta != null && request.hoursBagDelta !== 0) {
    details.push({
      label: t("wkt.client.vacations-diference-days"),
      value: formatSignedDays(request.hoursBagDelta),
      tone: getTone(request.hoursBagDelta),
    });
  }

  return details;
});

function formatProcedureTime(time?: ProcedureTimeDTO | null): string {
  return convertSimpleTimeToHHMM(time || null) || "";
}

function getAssistedVacationCountMode(): number {
  const vacationPolicy = sessionInformation.value?.employeeVacationPolicy;
  if (vacationPolicy?.assistedCountMode != null) {
    return vacationPolicy.assistedCountMode;
  }

  return sessionInformation.value?.company?.assistedVacationCountMode ?? VACATION_COUNT_NONE;
}

function shouldSeedManualVacationDays(): boolean {
  return (
    props.definition?.procedureClass === "VACATION_LEAVE" &&
    props.definition?.requestSubtype === REQUEST_SUBTYPE_COMPLETE_DAY &&
    Boolean(props.definition?.showVacationDays) &&
    getAssistedVacationCountMode() === VACATION_COUNT_NONE
  );
}

function createDraft(seed?: ProcedureRequestDialogDraftState | null): ProcedureRequestDialogDraft {
  if (seed) {
    return {
      requestTitle: seed.requestTitle || "",
      requestInfo: seed.requestInfo || "",
      fromDate: convertSimpleDateToDate(seed.fromDate),
      toDate: convertSimpleDateToDate(seed.toDate),
      fromTimeText: seed.fromTimeText || "",
      toTimeText: seed.toTimeText || "",
      vacationDaysText: seed.vacationDaysText || "",
      quantity: seed.quantity || "",
      projectId: seed.projectId ?? null,
      activityTypeId: seed.activityTypeId ?? null,
      hoursBagType: seed.hoursBagType ?? null,
      paidAbsenceCategoryId: seed.paidAbsenceCategoryId ?? null,
      approverComment: seed.approverComment || "",
    };
  }

  return {
    requestTitle: formState.value.values.requestTitle,
    requestInfo: formState.value.values.requestInfo,
    fromDate: convertSimpleDateToDate(formState.value.values.fromDate),
    toDate: convertSimpleDateToDate(formState.value.values.toDate),
    fromTimeText: formatProcedureTime(formState.value.values.fromHour),
    toTimeText: formatProcedureTime(formState.value.values.toHour),
    vacationDaysText:
      shouldSeedManualVacationDays() && props.request?.hoursBagDelta != null
        ? formatDecimal(Math.abs(props.request.hoursBagDelta))
        : "",
    quantity:
      formState.value.values.quantity == null
        ? ""
        : String(formState.value.values.quantity),
    projectId: formState.value.values.projectId,
    activityTypeId: formState.value.values.activityTypeId,
    hoursBagType: formState.value.values.hoursBagType,
    paidAbsenceCategoryId: formState.value.values.paidAbsenceCategoryId,
    approverComment: formState.value.values.approverComment,
  };
}

function buildDraftState(): ProcedureRequestDialogDraftState {
  return {
    requestTitle: draft.requestTitle,
    requestInfo: draft.requestInfo,
    fromDate: convertDateToSimpleDate(draft.fromDate),
    toDate: convertDateToSimpleDate(draft.toDate),
    fromTimeText: draft.fromTimeText,
    toTimeText: draft.toTimeText,
    vacationDaysText: draft.vacationDaysText,
    quantity: draft.quantity,
    projectId: draft.projectId,
    activityTypeId: draft.activityTypeId,
    hoursBagType: draft.hoursBagType,
    paidAbsenceCategoryId: draft.paidAbsenceCategoryId,
    approverComment: draft.approverComment,
  };
}

function asDateValue(
  value: Date | Date[] | Array<Date | null> | null | undefined
): Date | null {
  const resolvedValue = Array.isArray(value)
    ? value.find((item): item is Date => item instanceof Date) ?? null
    : value ?? null;

  return resolvedValue instanceof Date ? new Date(resolvedValue.getTime()) : null;
}

function parseTimeText(value: string): ProcedureTimeDTO | null {
  if (!value?.trim()) {
    return null;
  }

  const match = value.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);

  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return { hours, minutes, seconds: 0 };
}

function normalizeQuantity(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const normalized = Number(trimmed.replace(",", "."));
  return Number.isFinite(normalized) ? normalized : null;
}

function buildSubmitPayload(): ProcedureRequestDialogSubmitPayload | null {
  const quantity = normalizeQuantity(draft.quantity);
  const fromHour = parseTimeText(draft.fromTimeText);
  const toHour = parseTimeText(draft.toTimeText);

  if (formState.value.visibility.showFromDate && !draft.fromDate) {
    return null;
  }

  if (formState.value.visibility.showToDate && !draft.toDate) {
    return null;
  }

  if (formState.value.visibility.showFromTime && !fromHour) {
    return null;
  }

  if (formState.value.visibility.showToTime && !toHour) {
    return null;
  }

  if (formState.value.visibility.showQuantity && draft.quantity.trim().length > 0 && quantity == null) {
    return null;
  }

  if (
    vacationSupport.showManualDaysInput.value &&
    vacationSupport.manualDaysRequested.value == null
  ) {
    return null;
  }

  if (showHoursBagSelect.value && draft.hoursBagType == null) {
    return null;
  }

  if (showAbsenceCategorySelect.value && draft.paidAbsenceCategoryId == null) {
    return null;
  }

  if (
    vacationSupport.missingCalendarConfiguration.value &&
    openContext.value.mode !== "approve"
  ) {
    return null;
  }

  if (submitBlockedByBalance.value) {
    return null;
  }

  return {
    requestTitle: draft.requestTitle.trim(),
    requestInfo: draft.requestInfo.trim(),
    fromDate: convertDateToSimpleDate(draft.fromDate),
    toDate: convertDateToSimpleDate(draft.toDate),
    fromHour,
    toHour,
    quantity:
      formState.value.visibility.showQuantity
        ? quantity
        : vacationSupport.submitQuantity.value ?? quantity,
    projectId: draft.projectId,
    activityTypeId: draft.activityTypeId,
    hoursBagType: draft.hoursBagType,
    hoursBagDelta: vacationSupport.submitHoursBagDelta.value,
    paidAbsenceCategoryId: draft.paidAbsenceCategoryId,
    approverComment: draft.approverComment.trim(),
  };
}

function emitSubmit(): void {
  const payload = buildSubmitPayload();
  if (!payload) {
    return;
  }

  emit("submit", payload);
}

function emitDelete(): void {
  emit("delete");
}

function emitDecline(): void {
  const payload = buildSubmitPayload();
  if (!payload) {
    return;
  }

  emit("decline", payload);
}

function openAttachmentPicker(): void {
  attachmentInput.value?.click();
}

function handleAttachmentSelected(event: Event): void {
  const target = event.target as HTMLInputElement | null;
  const file = target?.files?.[0];

  if (!file) {
    return;
  }

  emit("upload-document", file);

  if (target) {
    target.value = "";
  }
}

function emitRemoveDocument(documentId?: number): void {
  if (!documentId) {
    return;
  }

  emit("remove-document", { documentId });
}

function downloadDocument(documentId?: number, documentName?: string | null): void {
  if (!documentId || !props.request?.id) {
    return;
  }

  gwtApi.downloadFile(
    `user/getRequestDocument/${props.request.id}/${documentId}`,
    documentName || "document"
  );
}

function openExpenseSheetDetail(): void {
  if (props.request?.expenseSheetId == null) {
    return;
  }

  emit("expense", props.request.expenseSheetId);
}

function formatSignedDays(days?: number | null): string {
  const value = days ?? 0;
  const absValue = Math.abs(value);
  const suffix =
    absValue === 1
      ? t("wkt.client.procedures.day")
      : t("wkt.client.procedures.days");

  return `${value} ${suffix}`;
}

function getTone(days?: number | null): "positive" | "negative" | undefined {
  if (days == null || days === 0) {
    return undefined;
  }

  return days > 0 ? "positive" : "negative";
}

function formatProjectLabel(
  shortName?: string | null,
  description?: string | null,
  fallback?: string | null
): string {
  const safeShortName = shortName?.trim();
  const safeDescription = description?.trim();

  if (safeShortName && safeDescription) {
    return `${safeShortName} - ${safeDescription}`;
  }

  return safeShortName || safeDescription || fallback || "---";
}

function formatDecimal(value?: number | null): string {
  if (value == null || Number.isNaN(value)) {
    return "0";
  }

  const locale = sessionInformation.value?.employee?.locale || undefined;
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatDaysAmount(value?: number | null): string {
  if (value == null) {
    return `-- ${t("wkt.client.procedures.days")}`;
  }

  const suffix =
    Math.abs(value) === 1
      ? t("wkt.client.procedures.day")
      : t("wkt.client.procedures.days");

  return `${formatDecimal(value)} ${suffix}`;
}

function formatTemplate(template: string, ...values: string[]): string {
  return values.reduce((result, value) => result.replace("%s", value), template);
}

function getHoursBagLabel(hoursBagType: number): string {
  const company = sessionInformation.value?.company as any;
  if (!company) {
    return String(hoursBagType);
  }

  switch (hoursBagType) {
    case 6:
      return company.defaultYearlyVacationLabel || String(hoursBagType);
    case 7:
      return company.defaultYearlyExtraLabel || String(hoursBagType);
    case 1:
      return company.defaultYearlyHoursBag1Label || String(hoursBagType);
    case 2:
      return company.defaultYearlyHoursBag2Label || String(hoursBagType);
    case 3:
      return company.defaultYearlyHoursBag3Label || String(hoursBagType);
    case 4:
      return company.defaultYearlyHoursBag4Label || String(hoursBagType);
    case 5:
      return company.defaultYearlyHoursBag5Label || String(hoursBagType);
    default:
      return String(hoursBagType);
  }
}
</script>

<style scoped>
:deep(.procedure-request-dialog.p-dialog) {
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
}

:deep(.procedure-request-dialog .p-dialog-header) {
  align-items: flex-start;
  padding: 1.2rem 1.35rem 0.95rem;
  border-bottom: 1px solid #e5edf5;
  background: linear-gradient(180deg, #fbfdff 0%, #f6f9fc 100%);
}

:deep(.procedure-request-dialog .p-dialog-content) {
  padding: 0 1.35rem 1.1rem;
  background: #f6f9fc;
  scrollbar-gutter: stable both-edges;
}

:deep(.procedure-request-dialog .p-dialog-footer) {
  padding: 0.95rem 1.35rem 1.15rem;
  border-top: 1px solid #e5edf5;
  background: #fbfdff;
  box-shadow: 0 -10px 24px rgba(15, 23, 42, 0.08);
}

:deep(.procedure-request-dialog .p-dialog-header-actions) {
  align-self: center;
}

:deep(.procedure-request-dialog .p-dialog-header-close-button) {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 999px;
  color: #607284;
}

:deep(.procedure-request-dialog .p-dialog-header-close-button:hover) {
  background: rgba(var(--primary-rgb), 0.08);
  color: var(--primary-color);
}

:deep(.procedure-request-dialog .p-tag) {
  border-radius: 999px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.procedure-request-dialog__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.procedure-request-dialog__header-copy {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  min-width: 0;
}

.procedure-request-dialog__icon {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--primary-rgb), 0.1);
  color: var(--primary-color);
  flex-shrink: 0;
}

.procedure-request-dialog__icon i {
  font-size: 1.05rem;
}

.procedure-request-dialog__title-block {
  min-width: 0;
}

.procedure-request-dialog__eyebrow {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #607284;
}

.procedure-request-dialog__title {
  margin: 0.25rem 0 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #12263a;
}

.procedure-request-dialog__body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  touch-action: pan-y;
  padding-top: 0.25rem;
  padding-bottom: 0.5rem;
  -webkit-overflow-scrolling: touch;
}

.procedure-request-dialog__state {
  min-height: 16rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.procedure-request-dialog__skeleton {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.procedure-request-dialog__skeleton-card {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1rem;
  border: 1px solid #d9e2ec;
  border-radius: 18px;
}

.procedure-request-dialog__skeleton-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.procedure-request-dialog__summary {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.procedure-request-dialog__meta-note {
  font-size: 0.85rem;
  color: #607284;
}

.procedure-request-dialog__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.procedure-request-dialog__cell,
.procedure-request-dialog__section {
  border: 1px solid #d9e2ec;
  border-radius: 18px;
  background: #fff;
}

.procedure-request-dialog__cell {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.95rem 1rem;
}

.procedure-request-dialog__label,
.procedure-request-dialog__section-title {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: #607284;
}

.procedure-request-dialog__value {
  color: #12263a;
  font-weight: 600;
  word-break: break-word;
}

.procedure-request-dialog__value--positive {
  color: #15803d;
}

.procedure-request-dialog__value--negative {
  color: #b42318;
}

.procedure-request-dialog__section {
  padding: 1rem;
}

.procedure-request-dialog__section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.procedure-request-dialog__form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
}

.procedure-request-dialog__field {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.procedure-request-dialog__field--full {
  grid-column: 1 / -1;
}

.procedure-request-dialog__field :deep(.p-inputtext),
.procedure-request-dialog__field :deep(.p-select),
.procedure-request-dialog__field :deep(.p-datepicker),
.procedure-request-dialog__field :deep(.p-textarea) {
  width: 100%;
  border-radius: 14px;
  border-color: #d7e2ee;
  background: #fff;
  box-shadow: none;
}

.procedure-request-dialog__field :deep(.p-inputtext:enabled:hover),
.procedure-request-dialog__field :deep(.p-select:not(.p-disabled):hover),
.procedure-request-dialog__field :deep(.p-datepicker:not(.p-disabled):hover),
.procedure-request-dialog__field :deep(.p-textarea:enabled:hover) {
  border-color: #bfd0e2;
}

.procedure-request-dialog__field :deep(.p-inputtext:enabled:focus),
.procedure-request-dialog__field :deep(.p-inputtext:enabled:focus-visible),
.procedure-request-dialog__field :deep(.p-select.p-focus),
.procedure-request-dialog__field :deep(.p-datepicker.p-focus),
.procedure-request-dialog__field :deep(.p-textarea:enabled:focus),
.procedure-request-dialog__field :deep(.p-textarea:enabled:focus-visible) {
  border-color: rgba(var(--primary-rgb), 0.5);
  box-shadow: 0 0 0 4px rgba(var(--primary-rgb), 0.12);
}

.procedure-request-dialog__field :deep(.p-select-label),
.procedure-request-dialog__field :deep(.p-inputtext),
.procedure-request-dialog__field :deep(.p-textarea) {
  color: #12263a;
}

.procedure-request-dialog__field :deep(.p-datepicker-input) {
  width: 100%;
}

.procedure-request-dialog__field :deep(.p-datepicker) {
  overflow: hidden;
}

.procedure-request-dialog__field :deep(.p-datepicker-input) {
  min-height: 2.75rem;
  border-radius: 14px;
}

.procedure-request-dialog__field :deep(.p-datepicker-input-icon-container),
.procedure-request-dialog__field :deep(.p-datepicker-input-icon) {
  color: #607284;
}

.procedure-request-dialog__field :deep(.p-datepicker-dropdown),
.procedure-request-dialog__field :deep(.p-select-dropdown) {
  color: #607284;
}

.procedure-request-dialog__field :deep(.p-datepicker.p-focus .p-datepicker-input-icon-container),
.procedure-request-dialog__field :deep(.p-datepicker.p-focus .p-datepicker-input-icon) {
  color: var(--primary-color);
}

.procedure-request-dialog__section-title {
  margin: 0 0 0.5rem;
}

.procedure-request-dialog__section-copy {
  margin: 0;
  color: #12263a;
  white-space: pre-wrap;
  word-break: break-word;
}

.procedure-request-dialog__vacation-summary {
  margin: 0.85rem 0 0;
  color: #425466;
  line-height: 1.55;
}

.procedure-request-dialog__vacation-balance {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9rem, 1fr));
  gap: 0.85rem;
  margin-top: 1rem;
}

.procedure-request-dialog__vacation-balance-cell {
  border: 1px solid #d9e2ec;
  border-radius: 14px;
  padding: 0.9rem 1rem;
  background: #f8fbff;
}

.procedure-request-dialog__vacation-balance-value {
  display: block;
  margin-top: 0.4rem;
  color: #12263a;
}

.procedure-request-dialog__vacation-balance-value--positive {
  color: #15803d;
}

.procedure-request-dialog__vacation-balance-value--negative {
  color: #b42318;
}

.procedure-request-dialog__warning {
  margin-top: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  background: #fff4f2;
  color: #b42318;
  font-size: 0.95rem;
  line-height: 1.5;
}

.procedure-request-dialog__hidden-input {
  display: none;
}

.procedure-request-dialog__documents-actions {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 0.85rem;
}

.procedure-request-dialog__documents {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.procedure-request-dialog__document {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid #d9e2ec;
  border-radius: 999px;
  background: #f8fbff;
  color: var(--primary-color);
  padding: 0.55rem 0.9rem;
  cursor: pointer;
}

.procedure-request-dialog__document-remove {
  align-self: center;
}

:deep(.procedure-request-dialog__action-button.p-button .p-button-label) {
  text-transform: uppercase;
  font-weight: 400;
}

.procedure-request-dialog__footer-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
}

.procedure-request-dialog__footer-spacer {
  flex: 1 1 auto;
  min-width: 0;
}

@media (max-width: 768px) {
  :deep(.procedure-request-dialog.p-dialog) {
    height: 100dvh;
    max-height: 100dvh !important;
    border-radius: 0 !important;
  }

  :deep(.procedure-request-dialog .p-dialog-header) {
    padding: 1rem 1rem 0.85rem;
  }

  :deep(.procedure-request-dialog .p-dialog-content) {
    padding: 0 1rem 1rem;
  }

  :deep(.procedure-request-dialog .p-dialog-footer) {
    padding: 0.85rem 1rem calc(0.95rem + env(safe-area-inset-bottom, 0px));
  }

  .procedure-request-dialog__body {
    height: calc(100dvh - 185px);
    max-height: 100%;
    padding-bottom: 0;
  }

  .procedure-request-dialog__footer-content {
    gap: 0.25rem;
  }

  .procedure-request-dialog__header {
    flex-direction: column;
    align-items: flex-start;
  }

  .procedure-request-dialog__form-grid,
  .procedure-request-dialog__skeleton-grid,
  .procedure-request-dialog__grid {
    grid-template-columns: 1fr;
  }
}
</style>
